import { customAgentResultItemId as taskItemId } from '@atbash-labs/ontology-agent-wire'
import type { AgentPriority, CustomAgentTask } from '@axilon-apps/domains-ontology-ui-state'

export type TaskStatus = 'open' | 'done' | 'rejected'

export const TASK_STATUSES: readonly TaskStatus[] = ['open', 'done', 'rejected']

/** Rejected wins over done: a rejected task is not something anyone did. */
export function taskStatus(task: CustomAgentTask): TaskStatus {
  if (task.approval_status === 'rejected') return 'rejected'
  if (task.work_status === 'done') return 'done'
  return 'open'
}

export { taskItemId }

export function parseTaskStatus(raw: string | null): TaskStatus {
  return raw === 'done' || raw === 'rejected' ? raw : 'open'
}

const PRIORITIES: readonly AgentPriority[] = ['critical', 'high', 'medium', 'low']

export function taskPriority(task: CustomAgentTask): AgentPriority | null {
  const raw = task.result.priority
  return typeof raw === 'string' && (PRIORITIES as readonly string[]).includes(raw)
    ? (raw as AgentPriority)
    : null
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
}

export function taskTitle(task: CustomAgentTask): string {
  return text(task.result.title) ?? text(task.result.name) ?? `Task ${task.position + 1}`
}

export function taskDetail(task: CustomAgentTask): string | null {
  return text(task.result.description) ?? text(task.result.detail)
}

/** Highest priority first, then newest. Unprioritized tasks sort last. */
export function sortTasks(tasks: readonly CustomAgentTask[]): CustomAgentTask[] {
  const rank = (task: CustomAgentTask) => {
    const priority = taskPriority(task)
    return priority === null ? PRIORITIES.length : PRIORITIES.indexOf(priority)
  }
  return [...tasks].sort((a, b) => rank(a) - rank(b) || b.time.localeCompare(a.time))
}

/** Stored step order, with stable item IDs breaking ties across refetches. */
export function sortTasksByPosition(tasks: readonly CustomAgentTask[]): CustomAgentTask[] {
  return [...tasks].sort(
    (a, b) => a.position - b.position || taskItemId(a).localeCompare(taskItemId(b))
  )
}

export function countTasksByStatus(tasks: readonly CustomAgentTask[]): Record<TaskStatus, number> {
  const counts: Record<TaskStatus, number> = { open: 0, done: 0, rejected: 0 }
  for (const task of tasks) counts[taskStatus(task)] += 1
  return counts
}

export interface TaskRunGroup {
  key: string
  tasks: CustomAgentTask[]
  counts: Record<TaskStatus, number>
}

/** Generic report tasks retain their run's step order and never infer a problem link. */
export function groupTasksByRun(
  tasks: readonly CustomAgentTask[],
  {
    status = 'open',
    retainedRunIds = new Set<string>()
  }: {
    status?: TaskStatus
    retainedRunIds?: ReadonlySet<string>
  } = {}
): TaskRunGroup[] {
  const byRun = new Map<string, TaskRunGroup>()
  for (const task of tasks) {
    const key = `run:${task.run_id}`
    let group = byRun.get(key)
    if (!group) {
      group = { key, tasks: [], counts: { open: 0, done: 0, rejected: 0 } }
      byRun.set(key, group)
    }
    group.tasks.push(task)
    group.counts[taskStatus(task)] += 1
  }
  const openedAt = new Map<string, number>()
  const groups: TaskRunGroup[] = []
  for (const group of byRun.values()) {
    if (
      !group.tasks.some(
        (task) =>
          taskStatus(task) === status ||
          (status === 'open' && retainedRunIds.has(task.run_id) && taskStatus(task) === 'done')
      )
    )
      continue
    // Use every step so completion or a tab change cannot reorder runs.
    openedAt.set(group.key, Math.min(...group.tasks.map((task) => Date.parse(task.time) || 0)))
    group.tasks = sortTasksByPosition(
      group.tasks.filter(
        (task) => taskStatus(task) === status || (status === 'open' && taskStatus(task) === 'done')
      )
    )
    groups.push(group)
  }
  return groups.sort(
    (a, b) => (openedAt.get(b.key) ?? 0) - (openedAt.get(a.key) ?? 0) || a.key.localeCompare(b.key)
  )
}

export function tasksSummary(counts: Record<TaskStatus, number>, highPriority: number): string {
  if (counts.open === 0) return 'Nothing open'
  const high = highPriority > 0 ? ` · ${highPriority} high priority` : ''
  return `${counts.open} open${high}`
}
